import React, { useMemo, useState } from 'react'
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Menu,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Theme,
    Typography,
    useTheme,
} from '@material-ui/core'
import { DateTime } from 'luxon'
import { toast } from 'react-toastify'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Container } from '../../../components'
import { axiosInstance } from '../../../helpers'
import { ProjectTagFilterSelect } from './ProjectTagFilterSelect'
import { useHistory } from 'react-router-dom'
import { BulkCreateProjectsModal } from './modals/BulkCreateProjectsModal'
import { BulkScheduleProjectsModal } from './modals/BulkScheduleProjectsModal'
import { RequestRFPModal } from '../../Workforce/RequestRFPModal'
import { Routes } from '../../../navigation/config/routes'
import {
    getTurnSetupStatus,
    TURN_SETUP_STATUSES,
    TurnSetupStatus,
    ScheduleTemplateReviewStatus,
} from './turnSetupStatus'

export type DraftReviewStatus = 'DRAFT' | 'CONFIRMED'
type BulkActionMode = 'create' | 'schedule' | 'delete' | 'rfp' | null
type SortKey = 'property' | 'start' | 'end'
type SortDir = 'asc' | 'desc'
type SetupStatusFilter = 'ALL' | TurnSetupStatus

const HEADER_CELL_STYLE: React.CSSProperties = {
    fontWeight: 700,
    fontSize: '0.875rem',
}

export interface BulkProperty {
    id: number
    name: string
}

interface ProjectDraftRow {
    id: number
    name: string
    schedule_id: string
    apartment_id: number
    apartment_name: string
    review_status: DraftReviewStatus
    schedule_review_status?: ScheduleTemplateReviewStatus
    start_date: string | null
    end_date: string | null
    project_scheduled: boolean
    /** Same rule as bulk-schedule candidates (`schedule_has_template_setup`). */
    has_template_setup?: boolean
    review_confirmed_at: string | null
    created_by_name: string | null
}

interface ProjectDraftsResponse {
    year: number
    summary: {
        total: number
        draft_count: number
        confirmed_count: number
        scheduled_count: number
        unscheduled_count: number
    }
    results: ProjectDraftRow[]
}

interface DeleteDraftsResponse {
    deleted: { schedule_id: number; name: string; apartment_name: string }[]
    skipped: { schedule_id: number; reason: string; name?: string }[]
}

interface PropertyRow {
    apartmentId: number
    apartmentName: string
    project: ProjectDraftRow | null
}

interface Props {
    properties: BulkProperty[]
    selectedTagIds: number[]
    onSelectedTagIdsChange: (tagIds: number[]) => void
    onClose: () => void
    theme: Theme
}

function currentTurnYear(): number {
    return DateTime.now().year
}

function formatDate(iso: string | null): string {
    if (!iso) return '—'
    const dt = DateTime.fromISO(iso)
    return dt.isValid ? dt.toFormat('LLL d, yyyy') : '—'
}

function compareNullableDates(
    a: string | null | undefined,
    b: string | null | undefined,
    dir: SortDir,
): number {
    const aMissing = a == null || a === ''
    const bMissing = b == null || b === ''
    if (aMissing && bMissing) return 0
    if (aMissing) return 1
    if (bMissing) return -1
    const aMs = DateTime.fromISO(a).toMillis()
    const bMs = DateTime.fromISO(b).toMillis()
    const cmp = aMs - bMs
    return dir === 'asc' ? cmp : -cmp
}

const StatTile = (props: { label: string; value: number }) => {
    const theme = useTheme()
    const { label, value } = props
    return (
        <Box
            style={{
                minWidth: 88,
                maxWidth: 130,
                padding: theme.spacing(0.5, 1),
                borderRadius: 6,
                border: `1px solid ${theme.palette.grey[300]}`,
                backgroundColor: theme.palette.background.paper,
                textAlign: 'center',
            }}
        >
            <Typography
                variant="subtitle1"
                style={{ fontWeight: 700, lineHeight: 1.15 }}
            >
                {value}
            </Typography>
            <Typography variant="caption" color="textSecondary">
                {label}
            </Typography>
        </Box>
    )
}

function isCreateEligible(row: PropertyRow): boolean {
    return row.project == null
}

function isScheduleEligible(row: PropertyRow): boolean {
    // Match list_bulk_schedule_candidates / schedule_has_template_setup.
    if (row.project == null) return false
    if (row.project.has_template_setup != null) {
        return !row.project.has_template_setup
    }
    // Older API responses: fall back to included-units only.
    return !row.project.project_scheduled
}

function isDeleteEligible(row: PropertyRow): boolean {
    return row.project?.review_status === 'DRAFT'
}

function hasScheduleSetup(
    project: ProjectDraftRow | null | undefined,
): boolean {
    if (project == null) return false
    if (project.has_template_setup != null) return project.has_template_setup
    return (
        project.project_scheduled ||
        project.schedule_review_status === 'PENDING'
    )
}

export const ManageBulkProjectsPanel: React.FC<Props> = ({
    properties,
    selectedTagIds,
    onSelectedTagIdsChange,
    onClose,
    theme,
}) => {
    const queryClient = useQueryClient()
    const history = useHistory()
    const thisYear = currentTurnYear()
    const yearOptions = [thisYear, thisYear + 1]

    const [year, setYear] = useState(thisYear)
    const [statusFilter, setStatusFilter] = useState<SetupStatusFilter>('ALL')
    const [sortKey, setSortKey] = useState<SortKey>('property')
    const [sortDir, setSortDir] = useState<SortDir>('asc')
    const [actionMode, setActionMode] = useState<BulkActionMode>(null)
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)

    const [createOpen, setCreateOpen] = useState(false)
    const [createWorkspaces, setCreateWorkspaces] = useState<BulkProperty[]>([])
    const [scheduleOpen, setScheduleOpen] = useState(false)
    const [scheduleIds, setScheduleIds] = useState<number[]>([])
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [pendingDeleteIds, setPendingDeleteIds] = useState<number[]>([])
    const [pendingDeleteSetupCount, setPendingDeleteSetupCount] = useState(0)
    const [rfpQueue, setRfpQueue] = useState<BulkProperty[]>([])
    const [rfpIndex, setRfpIndex] = useState(0)
    const [rfpOpen, setRfpOpen] = useState(false)

    const draftsQuery = useQuery({
        queryKey: ['project-drafts', year],
        queryFn: async () => {
            const response = await axiosInstance.get<ProjectDraftsResponse>(
                '/workorder/org-project-analytics/project-drafts/',
                { params: { year } },
            )
            return response.data
        },
    })

    const projectByApartment = useMemo(() => {
        const map = new Map<number, ProjectDraftRow>()
        for (const row of draftsQuery.data?.results ?? []) {
            map.set(row.apartment_id, row)
        }
        return map
    }, [draftsQuery.data])

    const allRows: PropertyRow[] = useMemo(
        () =>
            properties.map((p) => ({
                apartmentId: p.id,
                apartmentName: p.name,
                project: projectByApartment.get(p.id) ?? null,
            })),
        [properties, projectByApartment],
    )

    const summary = useMemo(() => {
        const counts: Record<TurnSetupStatus, number> = {
            'No Project': 0,
            Draft: 0,
            'Confirmed · Not Scheduled': 0,
            'Needs Schedule Review': 0,
            'Setup Complete': 0,
        }
        for (const row of allRows) {
            counts[getTurnSetupStatus(row.project)] += 1
        }
        return {
            totalProperties: allRows.length,
            ...counts,
        }
    }, [allRows])

    const eligibilityFiltered = useMemo(() => {
        if (actionMode === 'create') {
            return allRows.filter(isCreateEligible)
        }
        if (actionMode === 'schedule') {
            return allRows.filter(isScheduleEligible)
        }
        if (actionMode === 'delete') {
            return allRows.filter(isDeleteEligible)
        }
        if (actionMode === 'rfp') {
            return allRows
        }
        return allRows
    }, [allRows, actionMode])

    const displayedRows = useMemo(() => {
        const filtered = eligibilityFiltered.filter((row) => {
            if (statusFilter === 'ALL') return true
            return getTurnSetupStatus(row.project) === statusFilter
        })

        return [...filtered].sort((a, b) => {
            if (sortKey === 'property') {
                const cmp = a.apartmentName.localeCompare(
                    b.apartmentName,
                    undefined,
                    { sensitivity: 'base' },
                )
                return sortDir === 'asc' ? cmp : -cmp
            }
            if (sortKey === 'start') {
                return compareNullableDates(
                    a.project?.start_date,
                    b.project?.start_date,
                    sortDir,
                )
            }
            return compareNullableDates(
                a.project?.end_date,
                b.project?.end_date,
                sortDir,
            )
        })
    }, [eligibilityFiltered, statusFilter, sortKey, sortDir])

    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'))
            return
        }
        setSortKey(key)
        setSortDir('asc')
    }

    const deleteMutation = useMutation({
        mutationFn: async (scheduleIds: number[]) => {
            const response = await axiosInstance.post<DeleteDraftsResponse>(
                '/workorder/org-project-analytics/project-drafts/delete/',
                { schedule_ids: scheduleIds },
            )
            return response.data
        },
        onSuccess: (result) => {
            setSelectedIds(new Set())
            void queryClient.invalidateQueries({
                queryKey: ['project-drafts'],
                exact: false,
            })
            void queryClient.invalidateQueries({
                queryKey: ['bulk-schedule-candidates'],
                exact: false,
            })
            void queryClient.invalidateQueries({
                queryKey: ['workspace-project-analytics'],
                exact: false,
            })
            void queryClient.invalidateQueries({
                queryKey: ['project-analytics'],
                exact: false,
            })
            if (result.deleted.length > 0) {
                toast.success(
                    `Deleted ${result.deleted.length} draft${
                        result.deleted.length === 1 ? '' : 's'
                    }`,
                )
            }
            if (result.skipped.length > 0) {
                toast.warn(
                    `Skipped ${result.skipped.length}: ${result.skipped
                        .slice(0, 3)
                        .map((s) => s.reason)
                        .join(', ')}`,
                )
            }
            exitActionMode()
        },
        onError: () => {
            toast.error('Failed to delete drafts')
        },
    })

    const exitActionMode = () => {
        setActionMode(null)
        setSelectedIds(new Set())
    }

    const startActionMode = (mode: BulkActionMode) => {
        setMenuAnchor(null)
        setActionMode(mode)
        setSelectedIds(new Set())
        // Reset status filter so eligibility lists are not accidentally emptied.
        setStatusFilter('ALL')
    }

    const selectedCount = displayedRows.filter((r) =>
        selectedIds.has(r.apartmentId),
    ).length

    const toggleAll = (checked: boolean) => {
        if (!checked) {
            setSelectedIds(new Set())
            return
        }
        setSelectedIds(new Set(displayedRows.map((r) => r.apartmentId)))
    }

    const toggleRow = (apartmentId: number, checked: boolean) => {
        setSelectedIds((prev) => {
            const next = new Set(prev)
            if (checked) next.add(apartmentId)
            else next.delete(apartmentId)
            return next
        })
    }

    const handleDone = () => {
        const selected = displayedRows.filter((r) =>
            selectedIds.has(r.apartmentId),
        )
        if (selected.length === 0) {
            toast.error('Select at least one property')
            return
        }

        if (actionMode === 'create') {
            setCreateWorkspaces(
                selected.map((r) => ({
                    id: r.apartmentId,
                    name: r.apartmentName,
                })),
            )
            setCreateOpen(true)
            return
        }

        if (actionMode === 'schedule') {
            const ids = selected
                .map((r) => r.project?.id)
                .filter((id): id is number => id != null)
            setScheduleIds(ids)
            setScheduleOpen(true)
            return
        }

        if (actionMode === 'delete') {
            const ids = selected
                .map((r) => r.project?.id)
                .filter((id): id is number => id != null)
            const setupCount = selected.filter((r) =>
                hasScheduleSetup(r.project),
            ).length
            setPendingDeleteIds(ids)
            setPendingDeleteSetupCount(setupCount)
            setDeleteConfirmOpen(true)
            return
        }

        if (actionMode === 'rfp') {
            setRfpQueue(
                selected.map((r) => ({
                    id: r.apartmentId,
                    name: r.apartmentName,
                })),
            )
            setRfpIndex(0)
            setRfpOpen(true)
        }
    }

    const handleConfirmDelete = () => {
        if (pendingDeleteIds.length === 0) return
        deleteMutation.mutate(pendingDeleteIds, {
            onSuccess: () => {
                setDeleteConfirmOpen(false)
                setPendingDeleteIds([])
                setPendingDeleteSetupCount(0)
            },
        })
    }

    const handleCancelDelete = () => {
        if (deleteMutation.isLoading) return
        setDeleteConfirmOpen(false)
        setPendingDeleteIds([])
        setPendingDeleteSetupCount(0)
    }

    const actionLabel =
        actionMode === 'create'
            ? 'Create'
            : actionMode === 'schedule'
            ? 'Schedule'
            : actionMode === 'delete'
            ? 'Delete'
            : actionMode === 'rfp'
            ? 'Request RFP'
            : null

    const currentRfpProperty = rfpQueue[rfpIndex] ?? null

    const closeRfpWalk = () => {
        setRfpOpen(false)
        setRfpQueue([])
        setRfpIndex(0)
        exitActionMode()
    }

    const emptyMessage = (() => {
        if (actionMode === 'create') {
            return 'All properties already have a bulk project for this turn year.'
        }
        if (actionMode === 'schedule') {
            return 'No empty bulk projects ready to schedule for this turn year. Create projects first.'
        }
        if (actionMode === 'delete') {
            return 'No DRAFT bulk projects to delete for this turn year.'
        }
        if (actionMode === 'rfp') {
            return 'No properties available to request RFPs.'
        }
        if (statusFilter !== 'ALL') {
            return 'No properties match these filters.'
        }
        return 'No properties available.'
    })()

    return (
        <Container
            style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden',
            }}
        >
            <Box
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: theme.spacing(1),
                    padding: theme.spacing(1, 1, 0.5),
                }}
            >
                <Typography
                    variant="h6"
                    style={{ fontWeight: 700, flexShrink: 0 }}
                >
                    Manage Turn Projects
                </Typography>
                <Box
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: theme.spacing(1),
                        flex: 1,
                        minWidth: 0,
                    }}
                >
                    <StatTile
                        label="Properties"
                        value={summary.totalProperties}
                    />
                    <StatTile
                        label="No Project"
                        value={summary['No Project']}
                    />
                    <StatTile label="Draft" value={summary.Draft} />
                    <StatTile
                        label="Confirmed · Not Scheduled"
                        value={summary['Confirmed · Not Scheduled']}
                    />
                    <StatTile
                        label="Needs Schedule Review"
                        value={summary['Needs Schedule Review']}
                    />
                    <StatTile
                        label="Setup Complete"
                        value={summary['Setup Complete']}
                    />
                </Box>
                <Box
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.spacing(1),
                        flexShrink: 0,
                    }}
                >
                    <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        onClick={(e) => setMenuAnchor(e.currentTarget)}
                        disabled={!!actionMode}
                    >
                        Bulk Actions
                    </Button>
                    <Menu
                        anchorEl={menuAnchor}
                        open={Boolean(menuAnchor)}
                        onClose={() => setMenuAnchor(null)}
                        getContentAnchorEl={null}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        <MenuItem onClick={() => startActionMode('create')}>
                            Create
                        </MenuItem>
                        <MenuItem onClick={() => startActionMode('schedule')}>
                            Schedule
                        </MenuItem>
                        <MenuItem onClick={() => startActionMode('delete')}>
                            Delete
                        </MenuItem>
                        <MenuItem onClick={() => startActionMode('rfp')}>
                            Request RFP
                        </MenuItem>
                    </Menu>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={onClose}
                        style={{
                            borderWidth: 2,
                            borderColor: theme.palette.grey[700],
                            color: theme.palette.text.primary,
                            fontWeight: 600,
                        }}
                    >
                        Close
                    </Button>
                </Box>
            </Box>

            <Box
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: theme.spacing(1),
                    padding: theme.spacing(0.5, 1, 1),
                }}
            >
                <TextField
                    select
                    label="Turn Year"
                    value={year}
                    onChange={(e) => {
                        setYear(Number(e.target.value))
                        setSelectedIds(new Set())
                    }}
                    size="small"
                    variant="outlined"
                    style={{ minWidth: 120 }}
                    disabled={!!actionMode}
                >
                    {yearOptions.map((y) => (
                        <MenuItem key={y} value={y}>
                            {y}
                        </MenuItem>
                    ))}
                </TextField>
                <ProjectTagFilterSelect
                    selectedTagIds={selectedTagIds}
                    onChange={(ids) => {
                        onSelectedTagIdsChange(ids)
                        setSelectedIds(new Set())
                    }}
                />
                <TextField
                    select
                    label="Status"
                    value={statusFilter}
                    onChange={(e) => {
                        setStatusFilter(e.target.value as SetupStatusFilter)
                        setSelectedIds(new Set())
                    }}
                    size="small"
                    variant="outlined"
                    style={{ minWidth: 200 }}
                >
                    <MenuItem value="ALL">All</MenuItem>
                    {TURN_SETUP_STATUSES.map((status) => (
                        <MenuItem key={status} value={status}>
                            {status}
                        </MenuItem>
                    ))}
                </TextField>
            </Box>

            {actionMode && (
                <Box
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: theme.spacing(1),
                        padding: theme.spacing(1),
                        margin: theme.spacing(0, 1, 1),
                        border: `1px solid ${theme.palette.primary.main}`,
                        borderRadius: 6,
                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    }}
                >
                    <Box
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing(2),
                        }}
                    >
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                            Bulk {actionLabel}: {selectedCount} of{' '}
                            {displayedRows.length} selected
                        </Typography>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={
                                        displayedRows.length > 0 &&
                                        selectedCount === displayedRows.length
                                    }
                                    indeterminate={
                                        selectedCount > 0 &&
                                        selectedCount < displayedRows.length
                                    }
                                    onChange={(e) =>
                                        toggleAll(e.target.checked)
                                    }
                                    disabled={displayedRows.length === 0}
                                />
                            }
                            label="Select all"
                        />
                    </Box>
                    <Box
                        style={{
                            display: 'flex',
                            gap: theme.spacing(1),
                        }}
                    >
                        <Button size="small" onClick={exitActionMode}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            disabled={
                                selectedCount === 0 || deleteMutation.isLoading
                            }
                            onClick={handleDone}
                        >
                            {actionMode === 'delete'
                                ? deleteMutation.isLoading
                                    ? 'Deleting…'
                                    : 'Done'
                                : 'Done'}
                        </Button>
                    </Box>
                </Box>
            )}

            <Box style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                {draftsQuery.isLoading ? (
                    <Box
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            padding: theme.spacing(6),
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : draftsQuery.isError ? (
                    <Box style={{ padding: theme.spacing(3) }}>
                        <Typography color="error">
                            Failed to load bulk project data.
                        </Typography>
                    </Box>
                ) : (
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                {actionMode && (
                                    <TableCell
                                        padding="checkbox"
                                        style={HEADER_CELL_STYLE}
                                    />
                                )}
                                <TableCell style={HEADER_CELL_STYLE}>
                                    Status
                                </TableCell>
                                <TableCell
                                    sortDirection={
                                        sortKey === 'property' ? sortDir : false
                                    }
                                    style={HEADER_CELL_STYLE}
                                >
                                    <TableSortLabel
                                        active={sortKey === 'property'}
                                        direction={
                                            sortKey === 'property'
                                                ? sortDir
                                                : 'asc'
                                        }
                                        onClick={() => handleSort('property')}
                                    >
                                        Property
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell style={HEADER_CELL_STYLE}>
                                    Project
                                </TableCell>
                                <TableCell
                                    sortDirection={
                                        sortKey === 'start' ? sortDir : false
                                    }
                                    style={HEADER_CELL_STYLE}
                                >
                                    <TableSortLabel
                                        active={sortKey === 'start'}
                                        direction={
                                            sortKey === 'start'
                                                ? sortDir
                                                : 'asc'
                                        }
                                        onClick={() => handleSort('start')}
                                    >
                                        Start
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell
                                    sortDirection={
                                        sortKey === 'end' ? sortDir : false
                                    }
                                    style={HEADER_CELL_STYLE}
                                >
                                    <TableSortLabel
                                        active={sortKey === 'end'}
                                        direction={
                                            sortKey === 'end' ? sortDir : 'asc'
                                        }
                                        onClick={() => handleSort('end')}
                                    >
                                        End
                                    </TableSortLabel>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {displayedRows.map((row) => {
                                const project = row.project
                                return (
                                    <TableRow key={row.apartmentId} hover>
                                        {actionMode && (
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    color="primary"
                                                    checked={selectedIds.has(
                                                        row.apartmentId,
                                                    )}
                                                    onChange={(e) =>
                                                        toggleRow(
                                                            row.apartmentId,
                                                            e.target.checked,
                                                        )
                                                    }
                                                />
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            {getTurnSetupStatus(project)}
                                        </TableCell>
                                        <TableCell>
                                            {row.apartmentName}
                                        </TableCell>
                                        <TableCell>
                                            {project?.name ?? '—'}
                                        </TableCell>
                                        <TableCell>
                                            {formatDate(
                                                project?.start_date ?? null,
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {formatDate(
                                                project?.end_date ?? null,
                                            )}
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                            {displayedRows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={actionMode ? 6 : 5}>
                                        {emptyMessage}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </Box>

            <BulkCreateProjectsModal
                open={createOpen}
                theme={theme}
                workspaces={createWorkspaces}
                initialYear={year}
                lockYear
                hidePropertyPicker
                onClose={() => {
                    setCreateOpen(false)
                    void queryClient.invalidateQueries({
                        queryKey: ['project-drafts'],
                        exact: false,
                    })
                    exitActionMode()
                }}
            />

            <BulkScheduleProjectsModal
                open={scheduleOpen}
                theme={theme}
                initialYear={year}
                preferredScheduleIds={scheduleIds}
                restrictToPreferred
                lockYear
                onClose={() => {
                    setScheduleOpen(false)
                    void queryClient.invalidateQueries({
                        queryKey: ['project-drafts'],
                        exact: false,
                    })
                    void queryClient.invalidateQueries({
                        queryKey: ['bulk-schedule-candidates'],
                        exact: false,
                    })
                    exitActionMode()
                }}
            />

            <Dialog
                open={deleteConfirmOpen}
                onClose={handleCancelDelete}
                aria-labelledby="delete-turn-projects-title"
                aria-describedby="delete-turn-projects-description"
            >
                <DialogTitle id="delete-turn-projects-title">
                    Delete turn projects?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-turn-projects-description">
                        Are you sure you want to delete{' '}
                        {pendingDeleteIds.length} turn project
                        {pendingDeleteIds.length === 1 ? '' : 's'}? This cannot
                        be undone.
                    </DialogContentText>
                    {pendingDeleteSetupCount > 0 && (
                        <DialogContentText>
                            {pendingDeleteSetupCount} of these already{' '}
                            {pendingDeleteSetupCount === 1 ? 'has' : 'have'}{' '}
                            schedule setup (units, services, or dates) from a
                            prior turn. That setup will be permanently removed.
                        </DialogContentText>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCancelDelete}
                        disabled={deleteMutation.isLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        color="secondary"
                        variant="contained"
                        disabled={
                            deleteMutation.isLoading ||
                            pendingDeleteIds.length === 0
                        }
                        autoFocus
                    >
                        {deleteMutation.isLoading ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {currentRfpProperty && (
                <RequestRFPModal
                    open={rfpOpen}
                    apartmentId={currentRfpProperty.id}
                    apartmentName={currentRfpProperty.name}
                    initialYear={year}
                    queueLabel={
                        rfpQueue.length > 1
                            ? `${rfpIndex + 1} of ${rfpQueue.length}`
                            : undefined
                    }
                    onClose={closeRfpWalk}
                    onSent={() => {
                        if (rfpIndex + 1 < rfpQueue.length) {
                            setRfpIndex((index) => index + 1)
                            return
                        }
                        closeRfpWalk()
                        history.push(Routes.organization.rfp.rfpDashboard)
                    }}
                />
            )}
        </Container>
    )
}
